import { SimpleTextAreaControl } from "@/components/controls/SimpleSelectorControl";
import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import type { AssetLOTO } from "@/pages/asset-details/models/AssetLOTO";
import { updateAssetLOTOComments } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { ErrorModal, useToken } from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	type DialogProps,
	DialogTitle,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { createStyles, makeStyles } from "@mui/styles";
import Alert from "@mui/lab/Alert";
import moment from "moment";
import { type SyntheticEvent, useEffect, useState } from "react";

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			"& .input-group-text": {
				display: "none",
			},
			"& .form-control": {
				height: "120px !important",
			},
		},
		modalTitle: {
			backgroundColor: "#ced2cc",
			color: "#495057",
			textAlign: "center",
		},
		modalWarning: {
			color: "white",
			backgroundColor: "#dc3545",
			textAlign: "center",
		},
		messageTitle: {
			fontSize: 15,
			fontWeight: "bold",
			color: "#0000008A",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
	rowSelected: AssetLOTO;
	searchAssetLOTO: Function;
};

export const CommentsDialog = ({
	show,
	onHide,
	rowSelected,
	searchAssetLOTO,
}: Props) => {
	const classes = useStyles();
	const { getUserName } = useToken();
	const [editCommentSuccess, setEditCommentSuccess] = useState(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");

	const {
		state: { assetLOTOComments },
		actions: { setAssetLOTOComments },
	} = useAssetDetailsContext();
	//states
	const [commentInput, setCommentInput] = useState<string>("");

	//functions

	const appendComment = (newComment: string) => {
		const now = moment().format("DD/MM/YYYY HH:mm");
		let newValue = "";

		if (
			assetLOTOComments === "" ||
			assetLOTOComments === null ||
			assetLOTOComments === "null"
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue =
				assetLOTOComments +
				"\n" +
				getUserName() +
				" - " +
				now +
				" - " +
				newComment;
		}

		if (newComment != "") {
			setCommentInput("");
			setAssetLOTOComments(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(commentInput);
	};

	const onKeyDown = (e: any) => {
		if (e.key === "Enter") {
			appendComment(e.target.value);
			e.preventDefault();
		}
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setEditCommentSuccess(false);
	};

	const handleCancel = () => {
		onHide(false);
		setCommentInput("");
	};

	const handleSaveBtn = async () => {
		setIsSubmitLoading(true);
		const res = await updateAssetLOTOComments(
			rowSelected.AssetId,
			rowSelected.LOTONumber as string,
			assetLOTOComments,
		);
		if (res.ok) {
			searchAssetLOTO();
			setIsSubmitLoading(false);
			setEditCommentSuccess(true);
			onHide(true);
		} else {
			setError(res.message);
			setIsSubmitLoading(false);
		}
	};

	const changeComments = (value: string) => {
		setAssetLOTOComments(value);
	};

	useEffect(() => {
		if (rowSelected != null && show) {
			setAssetLOTOComments(rowSelected.Comments);
			setCommentInput("");
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<Dialog
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				onClose={() => handleCancel()}
			>
				<Grid2 container style={{ padding: "15px 0 0" }}>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<DialogTitle
							id="max-width-dialog-title"
							style={{ padding: "10px 24px 10px" }}
						>
							TITLE: {rowSelected?.LOTOName}
						</DialogTitle>
						<div style={{ padding: "0px 24px " }}>
							<p id="max-width-dialog-title" style={{ marginBottom: "0px" }}>
								{" "}
								ASSET: {rowSelected?.AssetName}
							</p>
						</div>
					</Grid2>
					<Grid2 container>
						<DialogContent dividers style={{ minHeight: "300px" }}>
							<Grid2 container>
								<Grid2
									style={{ marginTop: "15px", marginBottom: "15px" }}
									size={{ md: 12, xs: 12 }}
								>
									<Card
										variant="outlined"
										style={{ backgroundColor: "#E9F2F8", minHeight: "180px" }}
										className={classes.root}
									>
										<CardContent>
											<Typography className={classes.messageTitle}>
												Comments:
											</Typography>
											<SimpleTextAreaControl
												value={assetLOTOComments}
												onChange={changeComments}
												disabled
												showColor={false}
												styleText={{
													border: "none",
													backgroundColor: "#E9F2F8",
													fontSize: 16,
												}}
											/>
										</CardContent>
									</Card>
								</Grid2>
								<Grid2
									container
									spacing={2}
									justifyContent="center"
									alignItems="center"
								>
									<Grid2 size={{ md: 9, xs: 12 }}>
										<TextField
											variant="outlined"
											margin="dense"
											multiline
											maxRows={3}
											value={commentInput}
											onChange={(e) => setCommentInput(e.target.value)}
											label="Comment"
											fullWidth
											onKeyDown={onKeyDown}
											autoComplete="off"
										/>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="primary"
											onClick={addNewComment}
											style={{ height: "40px", marginTop: 2 }}
										>
											Add comment
										</Button>
									</Grid2>
								</Grid2>
							</Grid2>
						</DialogContent>
					</Grid2>

					<Grid2
						style={{ padding: "10px 0 15px", margin: "auto 0" }}
						size={{ md: 12, xs: 12 }}
					>
						<DialogActions style={{ padding: "0px 24px" }}>
							<Grid2 style={{ marginRight: "20px" }} size={{ md: 3, xs: 6 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={handleCancel}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 style={{ margin: 0 }} size={{ md: 3, xs: 6 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									onClick={handleSaveBtn}
								>
									Save
								</Button>
							</Grid2>
						</DialogActions>
					</Grid2>
				</Grid2>
			</Dialog>
			<Snackbar
				open={editCommentSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="info" onClose={handleClose}>
					Comment edited
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
